'use client';

type Listener = () => void;

const listeners = new Set<Listener>();

/** Stable empty params used for SSR / build-time prerender snapshots. */
export const PRODUCT_LISTING_SEARCH_PARAMS_SERVER_SNAPSHOT = new URLSearchParams();

let cachedSearchString: string | null = null;
let cachedSearchParams = new URLSearchParams();
let isHistoryPatchInstalled = false;
let isNotifyScheduled = false;

export function subscribeProductListingSearchParams(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getProductListingSearchParamsSnapshot(): URLSearchParams {
  if (typeof window === 'undefined') {
    return PRODUCT_LISTING_SEARCH_PARAMS_SERVER_SNAPSHOT;
  }

  const currentSearch = window.location.search;
  if (cachedSearchString !== currentSearch) {
    cachedSearchString = currentSearch;
    cachedSearchParams = new URLSearchParams(currentSearch);
  }

  return cachedSearchParams;
}

const flushProductListingSearchParamsListeners = () => {
  isNotifyScheduled = false;
  listeners.forEach(listener => listener());
};

export function notifyProductListingSearchParamsChanged(): void {
  // `location.search` is `''` when there is no query string, so that cannot be
  // the dirty sentinel — otherwise navigating from `?color=red` to a clean URL
  // keeps the previous URLSearchParams object.
  cachedSearchString = null;

  if (listeners.size === 0 || isNotifyScheduled) {
    return;
  }

  // Defer so pushState/replaceState callers inside useInsertionEffect (e.g. HeroUI)
  // do not synchronously trigger useSyncExternalStore updates.
  isNotifyScheduled = true;
  queueMicrotask(flushProductListingSearchParamsListeners);
}

export function ensureProductListingSearchParamsHistoryPatch(): void {
  if (typeof window === 'undefined' || isHistoryPatchInstalled) {
    return;
  }

  isHistoryPatchInstalled = true;

  window.addEventListener('popstate', notifyProductListingSearchParamsChanged);

  const originalPushState = history.pushState.bind(history);
  const originalReplaceState = history.replaceState.bind(history);

  history.pushState = (...args: Parameters<History['pushState']>) => {
    originalPushState(...args);
    notifyProductListingSearchParamsChanged();
  };

  history.replaceState = (...args: Parameters<History['replaceState']>) => {
    originalReplaceState(...args);
    notifyProductListingSearchParamsChanged();
  };
}
